import { Calendar } from '@untitledui/icons';
import type { Membership } from 'api-client';
import { type ReactNode } from 'react';

import { EventNameTemplateEditor } from '@/lib/crm/components/eventNameTemplate/EventNameTemplateEditor';
import {
  parseBookingQuestions,
  validateBookingQuestions,
} from '@/lib/crm/utils/bookingQuestions';
import {
  DEFAULT_WORKING_HOURS,
  parseWorkingHours,
  serializeWorkingHours,
  validateWorkingHours,
} from '@/lib/crm/utils/workingHours';
import { slugifyName, validateSlugLocal } from '@/lib/scheduling/slugConstants';
import type { SlugStatus } from '@/lib/scheduling/useSlugAvailability';

import type { CrmCreateConfig } from '../types';
import { BookingLinkPreviewButton } from './BookingLinkPreview';
import { DurationField } from './BookingTypeDurationField';
import { GlobalSlugField } from './GlobalSlugField';
import { SchedulingTypeField } from './SchedulingTypeField';
import { TeamMembersInput } from './TeamMembersInput';

// ============================================================================
// Module-scope constants
// ============================================================================

// Loose per-owner slug regex used for ahoy_slug. Matches the backend column
// (BookingTypeShortCode.slug, max_length=255) which has no reserved-word or
// 3–50 length constraint — those rules apply only to the globally-unique
// ahoy_global_slug, validated separately via validateSlugLocal below.
const BOOKING_SLUG_REGEX = /^[a-z0-9-]+$/;

export const DEFAULT_INITIAL_VALUES = {
  ahoy_name: '',
  ahoy_slug: '',
  ahoy_global_slug: '',
  ahoy_description: '',
  ahoy_duration_minutes: 30,
  ahoy_buffer_before_minutes: 0,
  ahoy_buffer_after_minutes: 0,
  ahoy_min_notice_hours: 24,
  ahoy_max_days_ahead: 60,
  ahoy_location_type: 'google_meet',
  ahoy_custom_location: '',
  ahoy_working_hours: serializeWorkingHours(DEFAULT_WORKING_HOURS),
  // ahoy_is_active is deliberately NOT here. It is not a form field, and
  // buildSubmitPayload drops every key that isn't a declared field, so a value
  // seeded here never reaches the create event. It is stamped in onSubmit.
  ahoy_scheduling_type: 'individual',
  _team_member_ids: [] as string[],
  ahoy_timezone: '',
  ahoy_event_name_template: '',
  ahoy_custom_questions: '',
  ahoy_hide_host_info: 'false',
};

// ============================================================================
// Module-scope helpers
// ============================================================================

function renderDurationFieldAsComponent(
  value: unknown,
  onChange: (next: unknown) => void,
  disabled: boolean
): ReactNode {
  return (
    <DurationField value={value} onChange={onChange} disabled={disabled} />
  );
}

function renderSchedulingTypeField(
  value: unknown,
  onChange: (next: unknown) => void,
  disabled: boolean
): ReactNode {
  return (
    <SchedulingTypeField
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

function renderEventNameTemplateField(
  value: unknown,
  onChange: (next: unknown) => void,
  disabled: boolean
): ReactNode {
  return (
    <EventNameTemplateEditor
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

// ============================================================================
// Factory
// ============================================================================

function buildBookingTypeCreateConfig(
  orgId: number,
  /**
   * Org-wide scheduling defaults, already mapped onto booking-type property
   * names by `useOrgSchedulingDefaults`. Empty until the org entity resolves,
   * or when the org has never saved any defaults.
   */
  orgSchedulingDefaults: Record<string, unknown>,
  members: ReadonlyArray<Membership>,
  createMutationFn: (vars: {
    properties: Record<string, unknown>;
    teamMemberUserIds?: string[];
  }) => Promise<{ entity_id: string }>,
  /** Called with the new booking link's id after a successful create. */
  onCreated?: (entityId: string) => void
): CrmCreateConfig {
  // Mutable holder — updated by GlobalSlugField.onStatusChange, read by validate().
  // Valid as a closure variable: buildBookingTypeCreateConfig is called once per slideout
  // mount and the closure is GC'd on unmount.
  const slugStatusHolder = { current: '' as SlugStatus | '' };

  // Refs that renderGlobalSlugFieldClosure populates on each render so that
  // handleSlugStatusChange can call onChange with the current slug value.
  // Calling onChange triggers setFormValues (new object reference) which forces
  // useFormErrors to recompute and pick up the updated status.
  const slugValueRef: { current: string } = { current: '' };
  const slugOnChangeRef: { current: ((next: unknown) => void) | null } = {
    current: null,
  };

  // Stable callback reference — avoids unstable prop reference which would cause
  // SlugAvailabilityField's useEffect([status, onStatusChange]) to fire on every render.
  const handleSlugStatusChange = (s: SlugStatus) => {
    slugStatusHolder.current = s;
    // Force useFormErrors to recompute by triggering a form value update.
    // setFormValues always creates a new object reference, so the memoized
    // errors will re-run config.validate() and pick up the updated status.
    slugOnChangeRef.current?.(slugValueRef.current);
  };

  function renderGlobalSlugFieldClosure(
    value: unknown,
    onChange: (next: unknown) => void,
    disabled: boolean
  ): ReactNode {
    slugValueRef.current = String(value ?? '');
    slugOnChangeRef.current = onChange;
    return (
      <GlobalSlugField
        value={value}
        onChange={onChange}
        disabled={disabled}
        onStatusChange={handleSlugStatusChange}
        orgId={orgId}
        aria-label='Public URL slug'
      />
    );
  }

  const renderTeamMembersAsComponent = (
    value: unknown,
    onChange: (next: unknown) => void,
    disabled: boolean
  ): ReactNode => (
    <TeamMembersInput
      value={value}
      onChange={onChange}
      disabled={disabled}
      members={members}
    />
  );

  return {
    orgId,
    entityType: 'ahoy_booking_type',
    title: 'New booking link',
    sectionStyle: 'accordion',
    submitLabel: 'Create link',
    // A booking link is configured once, not entered in bulk.
    showCreateAnother: false,
    eyebrow: (
      <>
        <Calendar className='size-4' />
        <span>Booking link</span>
      </>
    ),
    renderFooterSecondaryAction: (values: Record<string, unknown>) => (
      <BookingLinkPreviewButton values={values} />
    ),

    initialValues: {
      ...DEFAULT_INITIAL_VALUES,
      ...orgSchedulingDefaults,
    },

    sections: [
      {
        id: 'basics',
        title: 'Basics',
        defaultExpanded: true,
        fields: [
          {
            kind: 'property',
            property: 'ahoy_name',
            label: 'Name',
            required: true,
            helpText:
              'Shown to invitees as the title of your public booking page.',
          },
          {
            kind: 'custom',
            property: 'ahoy_global_slug',
            label: 'Public URL',
            render: renderGlobalSlugFieldClosure,
          },
          {
            kind: 'property',
            property: 'ahoy_description',
            label: 'Description',
            helpText: 'Shown to invitees on your public booking page.',
          },
          {
            kind: 'custom',
            property: 'ahoy_scheduling_type',
            label: 'Scheduling type',
            render: renderSchedulingTypeField,
          },
          {
            kind: 'custom',
            property: '_team_member_ids',
            label: 'Team members',
            render: renderTeamMembersAsComponent,
            condition: (values: Record<string, unknown>) =>
              values.ahoy_scheduling_type !== 'individual',
          },
          {
            kind: 'property',
            property: 'ahoy_timezone',
            label: 'Timezone',
            helpText: 'Timezone used for availability across team members.',
            condition: (values: Record<string, unknown>) =>
              values.ahoy_scheduling_type !== 'individual',
          },
        ],
      },
      {
        id: 'duration_availability',
        title: 'Duration & availability',
        fields: [
          {
            kind: 'custom',
            property: 'ahoy_duration_minutes',
            label: 'Duration',
            render: renderDurationFieldAsComponent,
          },
          // Buffer before/after carry no help text: the labels say it all.
          {
            kind: 'property',
            property: 'ahoy_buffer_before_minutes',
            label: 'Buffer before',
          },
          {
            kind: 'property',
            property: 'ahoy_buffer_after_minutes',
            label: 'Buffer after',
          },
          {
            kind: 'property',
            property: 'ahoy_min_notice_hours',
            label: 'Minimum notice',
            helpText: 'Hours of lead time before a booking',
          },
          {
            kind: 'property',
            property: 'ahoy_max_days_ahead',
            label: 'Availability window',
            helpText: 'Days into the future bookings are allowed',
          },
        ],
      },
      {
        id: 'location',
        title: 'Location',
        fields: [
          {
            kind: 'property',
            property: 'ahoy_location_type',
            label: 'Location type',
          },
          {
            kind: 'property',
            property: 'ahoy_custom_location',
            label: 'Zoom meeting URL',
            helpText:
              'Your personal Zoom meeting room URL (e.g. https://zoom.us/j/1234567890)',
            condition: (values: Record<string, unknown>) =>
              values.ahoy_location_type === 'zoom',
          },
          {
            kind: 'property',
            property: 'ahoy_custom_location',
            label: 'Custom location',
            condition: (values: Record<string, unknown>) =>
              values.ahoy_location_type === 'custom',
          },
        ],
      },
      {
        id: 'working_hours',
        title: 'Working hours',
        fields: [
          {
            kind: 'property',
            property: 'ahoy_working_hours',
            // Label intentionally left empty — the section title already
            // reads "Working hours" so a duplicate label wastes vertical
            // space above the day grid.
            label: '',
          },
        ],
      },
      {
        id: 'questions',
        title: 'Questions',
        fields: [
          {
            kind: 'property',
            property: 'ahoy_custom_questions',
            // Label and help text intentionally empty. The section title
            // already reads "Questions", and BookingQuestionsInput renders its
            // own single standing hint beneath the Add question button — a
            // label plus help text here duplicated both.
            label: '',
          },
        ],
      },
      {
        id: 'advanced',
        title: 'Advanced',
        fields: [
          {
            kind: 'custom',
            property: 'ahoy_event_name_template',
            label: 'Calendar event name template',
            render: renderEventNameTemplateField,
          },
          {
            kind: 'property',
            property: 'ahoy_hide_host_info',
            label: 'Hide host info',
            helpText:
              'Hide your name and photo from the public booking page and confirmation screen.',
          },
        ],
      },
    ],

    onCreated,

    validate: (values: Record<string, unknown>) => {
      const errors: Record<string, string> = {};

      const name = String(values.ahoy_name ?? '').trim();
      if (!name) {
        errors.ahoy_name = 'Name is required';
      }

      const slug = String(values.ahoy_slug ?? '');
      if (slug && !BOOKING_SLUG_REGEX.test(slug)) {
        errors.ahoy_slug =
          'Slug can only contain lowercase letters, numbers, and hyphens';
      }
      if (!slug && name && !slugifyName(name)) {
        errors.ahoy_name =
          errors.ahoy_name ?? 'Name must contain at least one letter or number';
      }

      // Validate the effective ahoy_global_slug — same value onSubmit will send,
      // including the auto-generated fallback from the name. Otherwise an invalid
      // auto-generated slug (e.g. from a name like "Hi") would bypass synchronous
      // validation and only fail server-side.
      const globalSlug =
        String(values.ahoy_global_slug ?? '') || slugifyName(name);
      if (globalSlug) {
        const local = validateSlugLocal(globalSlug);
        if (!local.isValid) {
          errors.ahoy_global_slug =
            local.reason === 'reserved'
              ? 'This slug is reserved.'
              : 'Use 3–50 lowercase letters, numbers, or hyphens. Must start with a letter or number.';
        } else {
          // Check async availability status (only when local validation passes,
          // so only one error is shown at a time)
          const asyncStatus = slugStatusHolder.current;
          if (asyncStatus === 'taken') {
            errors.ahoy_global_slug =
              'This URL is already in use. Please choose a different slug.';
          } else if (asyncStatus === 'error') {
            errors.ahoy_global_slug =
              'Unable to verify slug availability. Please try again.';
          }
        }
      }

      const duration = Number(values.ahoy_duration_minutes);
      if (!Number.isFinite(duration) || duration < 5 || duration > 480) {
        errors.ahoy_duration_minutes =
          'Duration must be between 5 and 480 minutes';
      }

      if (
        values.ahoy_location_type === 'custom' &&
        !String(values.ahoy_custom_location ?? '').trim()
      ) {
        errors.ahoy_custom_location = 'Custom location is required';
      }

      if (
        values.ahoy_location_type === 'zoom' &&
        !String(values.ahoy_custom_location ?? '').trim()
      ) {
        errors.ahoy_custom_location = 'Zoom meeting URL is required';
      }

      const workingHoursParsed = parseWorkingHours(
        String(values.ahoy_working_hours ?? '')
      );
      const workingHoursError = validateWorkingHours(workingHoursParsed);
      if (workingHoursError) {
        errors.ahoy_working_hours = workingHoursError;
      }

      const questionsError = validateBookingQuestions(
        parseBookingQuestions(values.ahoy_custom_questions)
      );
      if (questionsError) {
        errors.ahoy_custom_questions = questionsError;
      }

      const schedulingType = String(
        values.ahoy_scheduling_type ?? 'individual'
      );
      if (schedulingType !== 'individual') {
        const teamMembers = Array.isArray(values._team_member_ids)
          ? values._team_member_ids.filter(
              (id): id is string => typeof id === 'string'
            )
          : [];
        if (teamMembers.length === 0) {
          errors._team_member_ids =
            'At least one team member is required for round robin or collective scheduling';
        }
      }

      return errors;
    },

    // Receives the form's cleaned payload: property fields with empties
    // stripped, plus every custom field (`ahoy_global_slug`,
    // `ahoy_scheduling_type`, `_team_member_ids`, `ahoy_duration_minutes`,
    // `ahoy_event_name_template`) verbatim.
    onSubmit: async (values: Record<string, unknown>) => {
      const slug =
        String(values.ahoy_slug ?? '') ||
        slugifyName(String(values.ahoy_name ?? ''));

      const globalSlug =
        String(values.ahoy_global_slug ?? '') ||
        slugifyName(String(values.ahoy_name ?? ''));

      const rawTeamMembers = values._team_member_ids;
      const teamMemberUserIds = Array.isArray(rawTeamMembers)
        ? rawTeamMembers.filter((id): id is string => typeof id === 'string')
        : [];

      // Strip _team_member_ids from properties — team members are now associations.
      const { _team_member_ids: _ignored, ...propertiesWithoutTeamMembers } =
        values;
      void _ignored;

      return createMutationFn({
        properties: {
          ...propertiesWithoutTeamMembers,
          ahoy_slug: slug,
          ahoy_global_slug: globalSlug,
          // Every new booking link starts active. This must be stamped here,
          // not seeded via initialValues: the Booking Links list tabs filter on
          // ahoy_is_active = 'true', so a link created without it is invisible
          // in the CRM while its public page keeps working (anything other than
          // the string 'false' is treated as active there).
          ahoy_is_active: 'true',
        },
        teamMemberUserIds,
      });
    },
  };
}

export { buildBookingTypeCreateConfig };
